import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

// Marquee for the live strip. CSS transform animations composite the layer at
// fractional pixel offsets, which resamples the text every frame and makes it
// look fuzzy while moving (it snapped crisp when paused on hover). Driving it
// from rAF and rounding to WHOLE pixels keeps the glyphs on the pixel grid,
// and the speed is slow enough to actually read.
const TICKER_SPEED_PX_S = 35;
const TickerTrack: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(now - last, 100) / 1000;
      last = now;
      const el = trackRef.current;
      if (el && !pausedRef.current) {
        const half = el.scrollWidth / 2; // content is rendered twice for the loop
        offsetRef.current += TICKER_SPEED_PX_S * dt;
        if (half > 0 && offsetRef.current >= half) offsetRef.current -= half;
        el.style.transform = `translateX(-${Math.round(offsetRef.current)}px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      ref={trackRef}
      className="flex whitespace-nowrap w-max"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
    >
      {children}
    </div>
  );
};

/**
 * Main dashboard: "what's happening in the markets I work".
 * Scoped server-side to the user's own + starred projects, so a team
 * member sees their world, not the whole team's firehose.
 *
 *  ┌ ticker ──────────────────────────────────────────────┐  live strip
 *  ├ tiles ───────────────────────────────────────────────┤  act-on-this
 *  ├ signal feed ───────────────┬ fresh vacancies rail ───┤  the meat
 *  ├ projects with life signs ──┴──────────────────────────┤
 *  └ stale-data nudge ────────────────────────────────────┘
 */

type MainView = 'overview' | 'projects' | 'enrichment' | 'intelligence';

interface OverviewProps {
  onNavigate?: (view: MainView) => void;
}

interface TickerItem {
  type: 'job_change' | 'new_job';
  at: string;
  projectId: number;
  projectName: string;
  text: string;
  backfill?: string | null;
  jobUrl?: string | null;
}

const timeAgo = (iso: string) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const initials = (name: string) =>
  (name || '?').split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

const AV_COLORS = ['#2563EB', '#7C3AED', '#059669', '#D97706', '#DC2626', '#0891B2', '#4338CA', '#BE185D'];
const avColor = (name: string) => AV_COLORS[(name || '').length % AV_COLORS.length];

const Overview: React.FC<OverviewProps> = ({ onNavigate }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [feedFilter, setFeedFilter] = useState<'changes' | 'backfills'>('changes');

  useEffect(() => {
    fetch('/api/dashboard/overview', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = (user as any)?.first_name || (user as any)?.firstName || '';

  const movements = data?.movements || [];
  const jobs = data?.jobs || [];
  const ticker: TickerItem[] = data?.ticker || [];
  const tiles = data?.tiles || { backfills7d: 0, advertising7d: 0, jobChanges30d: 0 };
  const projects = data?.projects || [];
  const stale = data?.stale || { candidates: 0, projects: [] };

  // Every linked project gets a stable colour — the "Your projects" cards
  // are the key; every signal/vacancy carries its project's colour.
  const PROJECT_COLORS = ['#2563EB', '#7C3AED', '#059669', '#D97706', '#DC2626', '#0891B2', '#BE185D', '#4338CA'];
  const projectColor = useMemo(() => {
    const map = new Map<number, string>();
    projects.forEach((p: any, i: number) => map.set(p.id, PROJECT_COLORS[i % PROJECT_COLORS.length]));
    return (id: number) => map.get(id) || '#94A3B8';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  // Backfills view: company-centric — who is losing people, grouped, so it
  // reads differently from the person-centric job-changes list.
  const backfillGroups = useMemo(() => {
    const groups = new Map<string, { company: string; projectId: number; projectName: string; latest: string; people: any[] }>();
    for (const m of movements) {
      if (!m.from_company_name) continue;
      const key = `${m.project_id}:${m.from_company_name.toLowerCase()}`;
      if (!groups.has(key)) {
        groups.set(key, { company: m.from_company_name, projectId: m.project_id, projectName: m.project_name, latest: m.detected_at, people: [] });
      }
      groups.get(key)!.people.push(m);
    }
    return Array.from(groups.values()).sort((a, b) => b.people.length - a.people.length || new Date(b.latest).getTime() - new Date(a.latest).getTime());
  }, [movements]);

  const quiet = !loading && ticker.length === 0 && jobs.length === 0;

  return (
    <div className="min-h-full" style={{ background: 'linear-gradient(180deg,#F8FAFC 0%,#EEF2FF 130%)' }}>
      <style>{`
        @keyframes mm-pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
        .mm-live-dot { animation: mm-pulse 1.6s ease-in-out infinite; }
        @keyframes mm-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
        .mm-skeleton { background: linear-gradient(90deg,#EEF2F7 25%,#E2E8F0 37%,#EEF2F7 63%); background-size: 800px 100%; animation: mm-shimmer 1.4s linear infinite; }
      `}</style>

      {/* ── Live ticker ─────────────────────────────────────────────── */}
      <div className="w-full overflow-hidden" style={{ background: 'linear-gradient(90deg,#0F172A 0%,#1E1B4B 100%)' }}>
        <div className="flex items-center">
          <div className="flex items-center gap-2 px-4 py-2.5 flex-shrink-0 border-r border-white/10">
            <span className="mm-live-dot w-2 h-2 rounded-full bg-emerald-400 inline-block" />
            <span className="text-[11px] font-bold tracking-widest text-emerald-300 uppercase">Live</span>
          </div>
          <div className="relative flex-1 overflow-hidden">
            {ticker.length === 0 ? (
              <div className="py-2.5 px-4 text-xs text-slate-400">
                {loading ? 'Listening to your market…' : 'No market activity yet — enable monitoring or a job watch and signals appear here as they happen.'}
              </div>
            ) : (
              <TickerTrack>
                {[...ticker, ...ticker].map((t, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(`/project/${t.projectId}`)}
                    className="flex items-center gap-2 px-5 py-2.5 text-xs text-slate-200 hover:text-white transition-colors"
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: projectColor(t.projectId) }} title={t.projectName} />
                    <span className="font-medium">{t.text}</span>
                    <span className="text-slate-500">· {timeAgo(t.at)}</span>
                  </button>
                ))}
              </TickerTrack>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* ── Greeting ─────────────────────────────────────────────── */}
        <div className="mb-7">
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {greeting}{firstName ? `, ${firstName}` : ''}.
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {quiet
              ? 'Here’s your workspace at a glance.'
              : 'Here’s what moved in your market.'}
          </p>
        </div>

        {/* No explicit links yet: showing everything they own, with a nudge
            to star what they actually work so the dashboard focuses. */}
        {!loading && data?.scope === 'fallback' && (
          <div className="mb-6 flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3">
            <span className="text-lg">⭐</span>
            <p className="text-xs text-blue-900 flex-1">
              Showing all your projects for now. Star the ones you're working — in the
              <b> Projects</b> tab or when you open a project — and this dashboard focuses on just those.
            </p>
            <button onClick={() => navigate('/projects')} className="text-xs font-semibold text-blue-700 hover:text-blue-900 whitespace-nowrap">Choose projects →</button>
          </div>
        )}

        {/* ── Opportunity tiles ────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            {
              key: 'backfills' as const, n: tiles.backfills7d, label: 'Backfill opportunities', sub: 'companies losing people · 7 days',
              grad: 'linear-gradient(135deg,#FFF1F2 0%,#FFE4E6 100%)', ring: 'ring-rose-100', num: '#E11D48', blob: '#FDA4AF',
            },
            {
              key: 'advertising' as const, n: tiles.advertising7d, label: 'Companies advertising', sub: 'live vacancies found · 7 days',
              grad: 'linear-gradient(135deg,#F0F9FF 0%,#E0F2FE 100%)', ring: 'ring-sky-100', num: '#0284C7', blob: '#7DD3FC',
            },
            {
              key: 'changes' as const, n: tiles.jobChanges30d, label: 'Job changes', sub: 'people who moved · 30 days',
              grad: 'linear-gradient(135deg,#F5F3FF 0%,#EDE9FE 100%)', ring: 'ring-violet-100', num: '#7C3AED', blob: '#C4B5FD',
            },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => {
                if (t.key === 'advertising') {
                  document.getElementById('mm-jobs-rail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                  setFeedFilter(t.key === 'backfills' ? 'backfills' : 'changes');
                  document.getElementById('mm-signal-feed')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className={`relative overflow-hidden text-left rounded-2xl p-5 ring-1 ${t.ring} shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all`}
              style={{ background: t.grad }}
            >
              <div className="absolute -right-6 -top-8 w-28 h-28 rounded-full opacity-30 blur-2xl" style={{ background: t.blob }} />
              {loading ? (
                <div className="mm-skeleton h-9 w-16 rounded mb-2" />
              ) : (
                <div className="text-4xl font-extrabold tracking-tight" style={{ color: t.num }}>{t.n}</div>
              )}
              <div className="text-sm font-semibold text-gray-800 mt-1">{t.label}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{t.sub}</div>
              <div className="text-[11px] font-semibold mt-2" style={{ color: t.num }}>View →</div>
            </button>
          ))}
        </div>

        {/* ── Feed + vacancies rail ────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Signal feed */}
          <div id="mm-signal-feed" className="lg:col-span-2 bg-white rounded-2xl border border-gray-200/80 shadow-sm">
            <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Market signals</h2>
              <div className="flex gap-1 text-xs">
                {([['changes', 'Job changes'], ['backfills', 'Backfills']] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setFeedFilter(k)}
                    className={`px-2.5 py-1 rounded-full transition-colors ${feedFilter === k ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="divide-y divide-gray-50 max-h-[520px] overflow-y-auto">
              {loading && [1, 2, 3].map(i => (
                <div key={i} className="px-5 py-4"><div className="mm-skeleton h-4 w-3/4 rounded mb-2" /><div className="mm-skeleton h-3 w-1/2 rounded" /></div>
              ))}
              {!loading && movements.length === 0 && (
                <div className="px-5 py-10 text-center">
                  <div className="text-3xl mb-2">📡</div>
                  <p className="text-sm text-gray-600 font-medium">Monitoring is quiet.</p>
                  <p className="text-xs text-gray-400 mt-1">Job changes and backfill signals from your monitored projects land here the moment they're detected.</p>
                </div>
              )}

              {/* Job changes: person-centric, newest first */}
              {!loading && feedFilter === 'changes' && movements.map((m: any) => (
                <button key={m.id} onClick={() => navigate(`/project/${m.project_id}`)}
                  className="w-full text-left px-5 py-3.5 hover:bg-slate-50 transition-colors border-l-[3px]"
                  style={{ borderLeftColor: projectColor(m.project_id) }}>
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-bold text-white"
                      style={{ background: avColor(m.candidate_name) }}>
                      {initials(m.candidate_name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-800">
                        <b>{m.candidate_name}</b> left {m.from_company_name || 'their company'}
                        <span className="text-gray-400 mx-1">→</span>
                        joined <b>{m.to_company_name || 'a new company'}</b>
                        {m.to_position ? <span className="text-gray-500"> as {m.to_position}</span> : null}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[11px] text-gray-400">{timeAgo(m.detected_at)}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                          style={{ color: projectColor(m.project_id), background: `${projectColor(m.project_id)}14`, border: `1px solid ${projectColor(m.project_id)}33` }}>
                          {m.project_name}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}

              {/* Backfills: company-centric — who is losing people */}
              {!loading && feedFilter === 'backfills' && backfillGroups.length === 0 && movements.length > 0 && (
                <div className="px-5 py-8 text-center text-xs text-gray-400">No open seats detected in the last 30 days.</div>
              )}
              {!loading && feedFilter === 'backfills' && backfillGroups.map(g => (
                <button key={`${g.projectId}:${g.company}`} onClick={() => navigate(`/project/${g.projectId}`)}
                  className="w-full text-left px-5 py-3.5 hover:bg-rose-50/40 transition-colors border-l-[3px]"
                  style={{ borderLeftColor: projectColor(g.projectId) }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 font-bold">🎯 {g.company}</p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        lost <b>{g.people.length}</b> {g.people.length === 1 ? 'person' : 'people'} in 30 days —
                        {' '}{g.people.slice(0, 3).map((p: any) => p.candidate_name).join(', ')}{g.people.length > 3 ? ` +${g.people.length - 3} more` : ''}
                      </p>
                      {g.people[0]?.from_position && (
                        <span className="inline-flex mt-1.5 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-100 text-[11px] font-medium text-rose-700">
                          Likely open: {g.people[0].from_position}
                        </span>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[11px] text-gray-400">latest {timeAgo(g.latest)}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                          style={{ color: projectColor(g.projectId), background: `${projectColor(g.projectId)}14`, border: `1px solid ${projectColor(g.projectId)}33` }}>
                          {g.projectName}
                        </span>
                      </div>
                    </div>
                    <span className="flex-shrink-0 text-lg font-extrabold text-rose-600">{g.people.length}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Fresh vacancies rail */}
          <div id="mm-jobs-rail" className="rounded-2xl p-5 border border-sky-100 shadow-sm"
            style={{ background: 'linear-gradient(180deg,#F0F9FF 0%,#FFFFFF 70%)' }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-gray-900">Fresh vacancies</h2>
              <span className="text-[11px] font-semibold text-sky-600 bg-sky-100 rounded-full px-2 py-0.5">last 7 days</span>
            </div>
            {loading && [1, 2, 3].map(i => <div key={i} className="mm-skeleton h-12 rounded-xl mb-2" />)}
            {!loading && jobs.length === 0 && (
              <p className="text-xs text-gray-500 py-6 text-center">No new vacancies this week.<br />Job watches drop new postings here as they're found.</p>
            )}
            <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
              {jobs.map((j: any) => (
                <button key={j.id} onClick={() => navigate(`/project/${j.project_id}`)}
                  className="w-full text-left flex items-center gap-3 bg-white rounded-full border border-gray-200/70 pl-2 pr-4 py-2 hover:border-sky-300 hover:shadow-sm transition-all">
                  <span className="w-8 h-8 rounded-full text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0"
                    style={{ background: projectColor(j.project_id) }} title={j.project_name}>
                    {initials(j.company_name)}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-gray-900 truncate">{j.job_title || 'New role'}</span>
                    <span className="block text-[11px] text-gray-500 truncate">{j.company_name} · {timeAgo(j.first_seen_at)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Projects with life signs ─────────────────────────────── */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-baseline gap-2">
              <h2 className="font-bold text-gray-900">Your projects</h2>
              <span className="text-[11px] text-gray-400">each project's colour marks its signals above</span>
            </div>
            <button onClick={() => navigate('/projects')} className="text-xs font-semibold text-blue-600 hover:text-blue-800">All projects →</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading && [1, 2, 3, 4].map(i => <div key={i} className="mm-skeleton h-32 rounded-2xl" />)}
            {!loading && projects.map((p: any) => (
              <button key={p.id} onClick={() => navigate(`/project/${p.id}`)}
                className="text-left bg-white rounded-2xl border border-gray-200/80 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all border-t-4"
                style={{ borderTopColor: projectColor(p.id) }}>
                <div className="flex items-start gap-2">
                  <span className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0" style={{ background: projectColor(p.id) }} />
                  <span className="text-sm font-bold text-gray-900 truncate">{p.starred ? '★ ' : ''}{p.name}</span>
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">active {timeAgo(p.last_activity)}</div>
                <div className="flex items-baseline gap-1.5 mt-2">
                  <span className="text-xl font-extrabold text-gray-900">{parseInt(p.candidate_count).toLocaleString('en-GB')}</span>
                  <span className="text-[11px] text-gray-500">candidates</span>
                  {parseInt(p.added_7d) > 0 && (
                    <span className="text-[11px] font-semibold text-emerald-600">+{parseInt(p.added_7d).toLocaleString('en-GB')} this week</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {p.monitoring_enabled ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Monitoring
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-400 border border-gray-200"
                      title="This project isn't monitored — job changes here go unnoticed">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-300" /> Not monitored
                    </span>
                  )}
                  {parseInt(p.active_watches) > 0 && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
                      💼 {p.active_watches} watch{parseInt(p.active_watches) === 1 ? '' : 'es'}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Going stale nudge ────────────────────────────────────── */}
        {!loading && stale.candidates > 0 && (
          <div className="rounded-2xl border border-amber-200/80 p-5 mb-8 flex items-start gap-4"
            style={{ background: 'linear-gradient(90deg,#FFFBEB 0%,#FEF3C7 130%)' }}>
            <span className="text-2xl">⏳</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {stale.candidates.toLocaleString('en-GB')} profiles haven't been checked in over 60 days.
              </p>
              <p className="text-xs text-amber-800/80 mt-0.5">
                {stale.projects.map((s: any) => s.name).join(', ')} {stale.projects.length === 1 ? 'isn’t' : 'aren’t'} monitored —
                people change jobs roughly every 2-3 years, so unmonitored data quietly drifts out of date.
              </p>
            </div>
            <button
              onClick={() => navigate(`/project/${stale.projects[0]?.id}`)}
              className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors">
              Enable monitoring
            </button>
          </div>
        )}

        <div className="pb-10" />
      </div>
    </div>
  );
};

export default Overview;
